// Admin Fixed Expenses Page
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Edit2, Trash2, Building2, DollarSign, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'renta', label: 'Renta' },
  { value: 'servicios', label: 'Servicios (Luz, Agua, Gas)' },
  { value: 'nomina', label: 'Nómina Fija' },
  { value: 'seguros', label: 'Seguros' },
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'general', label: 'General' },
];

export default function AdminFixedExpenses() {
  const { user, getAuthHeaders } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    monthly_amount: '',
    branch_id: '',
    category: 'general',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, branchesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/fixed-expenses`, getAuthHeaders()),
        axios.get(`${API}/branches`, getAuthHeaders()),
        axios.get(`${API}/fixed-expenses/summary`, getAuthHeaders()),
      ]);
      setExpenses(expensesRes.data);
      setBranches(branchesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.monthly_amount) {
      toast.error('Nombre y monto son requeridos');
      return;
    }

    try {
      const payload = {
        ...formData,
        monthly_amount: parseFloat(formData.monthly_amount),
        branch_id: formData.branch_id || null,
      };

      if (editingExpense) {
        await axios.put(`${API}/fixed-expenses/${editingExpense.id}`, payload, getAuthHeaders());
        toast.success('Gasto actualizado');
      } else {
        await axios.post(`${API}/fixed-expenses`, payload, getAuthHeaders());
        toast.success('Gasto creado');
      }
      
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name,
      monthly_amount: expense.monthly_amount.toString(),
      branch_id: expense.branch_id || '',
      category: expense.category || 'general',
      notes: expense.notes || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto fijo?')) return;
    
    try {
      await axios.delete(`${API}/fixed-expenses/${id}`, getAuthHeaders());
      toast.success('Gasto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      monthly_amount: '',
      branch_id: '',
      category: 'general',
      notes: '',
    });
    setEditingExpense(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const getCategoryLabel = (value) => {
    return CATEGORIES.find(c => c.value === value)?.label || value;
  };

  if (user?.role !== 'master_admin') {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-zinc-500">Solo el Master Admin puede acceder a esta sección</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-fixed-expenses">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading text-white">Gastos Fijos</h1>
            <p className="text-zinc-500 text-sm mt-1">
              Administra los gastos fijos mensuales por sucursal
            </p>
          </div>
          <Button
            onClick={openNewDialog}
            className="bg-gold text-black hover:bg-gold-dark"
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Gasto
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Total Mensual</p>
                  <p className="text-2xl font-heading text-red-500">
                    ${summary.total_monthly?.toLocaleString('es-MX') || '0'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Gastos Globales</p>
                  <p className="text-2xl font-heading text-amber-500">
                    ${summary.global_expenses?.total?.toLocaleString('es-MX') || '0'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Por Sucursal</p>
                  <p className="text-2xl font-heading text-blue-500">
                    {summary.by_branch?.length || 0} sucursales
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expenses Table */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Nombre</TableHead>
                <TableHead className="text-zinc-400">Categoría</TableHead>
                <TableHead className="text-zinc-400">Sucursal</TableHead>
                <TableHead className="text-zinc-400 text-right">Monto Mensual</TableHead>
                <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-zinc-500 py-8">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-zinc-500 py-8">
                    No hay gastos fijos registrados
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => (
                  <TableRow key={expense.id} className="border-zinc-800">
                    <TableCell className="text-white font-medium">
                      {expense.name}
                      {expense.notes && (
                        <p className="text-xs text-zinc-500 mt-1">{expense.notes}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-300">
                        {getCategoryLabel(expense.category)}
                      </span>
                    </TableCell>
                    <TableCell className="text-zinc-400">
                      {expense.branch_name || (
                        <span className="text-amber-500">Todas las sucursales</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-red-400 font-semibold">
                      ${expense.monthly_amount.toLocaleString('es-MX')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(expense)}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(expense.id)}
                          className="text-zinc-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* By Branch Summary */}
        {summary?.by_branch && summary.by_branch.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <h3 className="font-heading text-white mb-4">Desglose por Sucursal</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.by_branch.map((branch) => (
                <div key={branch.branch_id} className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-white font-medium">{branch.branch_name}</p>
                  <p className="text-red-400 text-lg font-heading mt-1">
                    ${branch.total.toLocaleString('es-MX')}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {branch.expenses.map((exp, idx) => (
                      <li key={idx} className="text-xs text-zinc-500 flex justify-between">
                        <span>{exp.name}</span>
                        <span>${exp.amount.toLocaleString('es-MX')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white">
                {editingExpense ? 'Editar Gasto Fijo' : 'Nuevo Gasto Fijo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-zinc-400 block mb-1">
                  Nombre del Gasto *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Renta Local"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="expense-name-input"
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1">
                  Monto Mensual *
                </label>
                <Input
                  type="number"
                  value={formData.monthly_amount}
                  onChange={(e) => setFormData({ ...formData, monthly_amount: e.target.value })}
                  placeholder="15000"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="expense-amount-input"
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1">
                  Categoría
                </label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1">
                  Sucursal
                </label>
                <Select
                  value={formData.branch_id || "all"}
                  onValueChange={(value) => setFormData({ ...formData, branch_id: value === "all" ? "" : value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Todas las sucursales" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="all" className="text-amber-500">
                      Todas las sucursales
                    </SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id} className="text-white">
                        {branch.business_name} - {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-600 mt-1">
                  Dejar vacío si aplica a todas las sucursales
                </p>
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1">
                  Notas (opcional)
                </label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notas adicionales..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  className="flex-1 border-zinc-700 text-zinc-300"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gold text-black hover:bg-gold-dark"
                  data-testid="save-expense-btn"
                >
                  {editingExpense ? 'Actualizar' : 'Guardar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
